import React from "react";
import { Eye, ExternalLink, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Reusable PDF preview block (iframe + drive link header).
 * Used inline by PdfItem in list view, and full-width by PdfLibrary
 * when a grid/featured card is expanded.
 *
 * The preview surface (iframe + its padded wrapper) is intentionally
 * transparent so the preview blends into whatever surface it's placed on
 * in both light and dark modes. Only the toolbar header keeps a solid fill.
 */
export function PdfPreviewBlock({ pdf, onClose, fullWidth = false, className = "" }) {
  if (!pdf) return null;
  return (
    <div
      className={`rounded-2xl border border-slate-200 overflow-hidden fade-up shadow-sm bg-transparent ${className}`}
      data-testid={`preview-block-${pdf.id}`}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-white gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 shrink-0">
            <Eye className="h-3.5 w-3.5" /> Live Preview
          </span>
          {fullWidth && (
            <span
              className="text-sm font-semibold text-slate-700 truncate"
              data-testid={`preview-title-${pdf.id}`}
            >
              · {pdf.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <a
            href={pdf.googleDriveLink}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-brand-primary hover:text-brand-primary inline-flex items-center gap-1"
          >
            Open in Drive <ExternalLink className="h-3 w-3" />
          </a>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900"
              aria-label="Close preview"
              data-testid={`preview-close-${pdf.id}`}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="p-2 sm:p-3 bg-transparent">
        <iframe
          src={pdf.embedLink}
          title={pdf.title}
          className={`block w-full bg-transparent rounded-xl border border-slate-200 ${fullWidth ? "h-[640px] sm:h-[720px]" : "h-[520px]"}`}
          allow="autoplay"
        />
      </div>
    </div>
  );
}
